import { Component, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CalculationPdfExportService } from '../../services/calculation-pdf-export.service';
import { SubscriptionService } from '../../services/subscription.service';


@Component({
  selector: 'app-export-calculation-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    @if (isOpen()) {
      <div class="dialog-overlay" (click)="close()">
        <div class="dialog-content" (click)="$event.stopPropagation()">
          <div class="dialog-header">
            <h2>PDF-Export</h2>
            <button class="close-btn" (click)="close()" aria-label="Schließen">&times;</button>
          </div>
          
          <div class="dialog-body">
            <p class="info-text">
              Die Berechnung wird als PDF exportiert.
            </p>

            <div class="form-group checkbox-group">
              <label class="checkbox-label">
                <input 
                  type="checkbox" 
                  [checked]="includeHelpTexts()"
                  (change)="includeHelpTexts.set($any($event.target).checked)"
                />
                <span>Hilfetexte einschließen</span>
              </label>
              <p class="help-hint">
                Hilfetexte werden rechts neben den Feldern angezeigt.
              </p>
            </div>
          </div>
          
          <div class="dialog-footer">
            <button class="btn btn-secondary" (click)="close()">Abbrechen</button>
            <button 
              class="btn btn-primary" 
              (click)="export()"
              [disabled]="isExporting()"
            >
              {{ isExporting() ? 'Wird exportiert...' : 'PDF exportieren' }}
            </button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .dialog-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }

    .dialog-content {
      background: white;
      border-radius: 8px;
      padding: 0;
      width: 90%;
      max-width: 500px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    }

    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      border-bottom: 1px solid #e5e7eb;
    }

    .dialog-header h2 {
      margin: 0;
      font-size: 18px;
      color: #1f2937;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      color: #6b7280;
      padding: 0;
      width: 30px;
      height: 30px;
    }

    .dialog-body {
      padding: 20px;
    }

    .info-text {
      margin: 0 0 20px 0;
      color: #6b7280;
      font-size: 14px;
    }

    .checkbox-group {
      margin: 0;
    }

    .checkbox-label {
      display: flex;
      align-items: center;
      cursor: pointer;
      font-size: 14px;
      color: #1f2937;
      margin-bottom: 8px;
    }

    .checkbox-label input {
      margin-right: 10px;
      cursor: pointer;
      width: 16px;
      height: 16px;
    }

    .help-hint {
      margin: 8px 0 0 31px;
      font-size: 12px;
      color: #9ca3af;
    }

    .dialog-footer {
      display: flex;
      gap: 10px;
      justify-content: flex-end;
      padding: 15px 20px;
      border-top: 1px solid #e5e7eb;
    }

    .btn {
      padding: 8px 16px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-weight: 500;
      font-size: 14px;
    }

    .btn-primary {
      background: #5445a6;
      color: white;
    }

    .btn-primary:hover:not(:disabled) {
      background: #3d3280;
    }

    .btn-primary:disabled {
      background: #c4b5fd;
      cursor: not-allowed;
    }

    .btn-secondary {
      background: #e5e7eb;
      color: #1f2937;
    }

    .btn-secondary:hover {
      background: #d1d5db;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportCalculationDialogComponent {
  isOpen = signal(false);
  includeHelpTexts = signal(false);
  isExporting = signal(false);
  onToast = signal<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
  
  private calculationId: number | null = null;
  private pdfExportService = inject(CalculationPdfExportService);
  private subscriptionService = inject(SubscriptionService);

  open(calculationId: number): void {
    this.calculationId = calculationId;
    this.includeHelpTexts.set(false);
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
    this.isExporting.set(false);
  }

  export(): void {
    if (!this.calculationId) return;

    // Feature Check
    if (!this.subscriptionService.hasFeature('PDF_EXPORT')) {
      this.onToast.set({ message: 'Diese Funktion ist in deinem aktuellen Abonnement nicht verfügbar. Aktualisiere dein Abonnement, um diese Funktion freizuschalten.', type: 'warning' });
      return;
    }

    this.isExporting.set(true);

    this.pdfExportService
      .exportCalculationPdf(this.calculationId, this.includeHelpTexts())
      .subscribe({
        next: (blob) => {
          if (blob.size === 0) {
            console.error('PDF blob is empty');
            alert('Fehler: PDF konnte nicht erstellt werden (leer)');
            this.isExporting.set(false);
            return;
          }
          const now = new Date();
          const dateStr = now.toISOString().slice(0, 10);
          const timeStr = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
          const fileName = `Kalkulation_Export_${dateStr}_${timeStr}.pdf`;
          this.pdfExportService.downloadFile(blob, fileName);
          this.close();
        },
        error: (error) => {
          console.error('PDF export failed:', error);
          const errorMsg = error?.error?.message || error?.message || 'Fehler beim PDF-Export';
          alert(`Fehler: ${errorMsg}`);
          this.isExporting.set(false);
        }
      });
  }
}
